import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import clsx from 'clsx'

/**
 * A link that displays text with a follow arrow (→), handling two layout modes:
 *
 * - Not clamped: text fits within `lines` lines. Uses dome-link-arrow-right CSS ::after so the
 *                arrow flows inline after the last word.
 * - Clamped:     text exceeds `lines` lines and is truncated. Shows "… →" absolutely positioned
 *                at the bottom-right over the truncation point. Takes no space from line 1.
 *
 * Uses useLayoutEffect to measure before paint (no flash on text change), and a ResizeObserver
 * to re-measure on container/layout resizes. Temporarily applies inline styles to detect
 * overflow, then clears them so CSS classes take over.
 *
 * @prop {string}  text    - The text to display.
 * @prop {string}  href    - The link URL.
 * @prop {string}  target  - The link target (e.g. "_blank").
 * @prop {number}  lines   - Max lines before clamping. Must be >= 1. Defaults to 2.
 */
const ClampedLink = ({ text, href, target, lines = 2 }) => {
  const linkRef = useRef(null)
  const textRef = useRef(null)
  const [isClamped, setIsClamped] = useState(false)

  const measure = useCallback(() => {
    const el = textRef.current
    if (!el) return

    // Reset span to inline for measurement
    el.style.display = 'inline'
    el.style.overflow = ''
    el.style.webkitLineClamp = ''
    el.style.webkitBoxOrient = ''

    // Temporarily append the arrow character so height includes it — this prevents
    // the arrow from wrapping to a new line undetected when text nearly fills a line.
    const arrowNode = document.createTextNode('\u00A0\u2192')
    el.appendChild(arrowNode)
    const lineHeight = parseFloat(getComputedStyle(el).lineHeight)
    // +1 tolerance for sub-pixel rounding (offsetHeight is integer, lineHeight can be fractional)
    const offsetHeight = el.offsetHeight
    const nextClamped = offsetHeight > lines * lineHeight + 1
    el.removeChild(arrowNode)

    setIsClamped(nextClamped)

    // Restore final clamped state directly — not via React style prop, since measure()
    // resets these each run and React may bail out of re-rendering on unchanged state.
    el.style.display = ''
    el.style.overflow = ''
    el.style.webkitLineClamp = nextClamped ? String(lines) : ''
    el.style.webkitBoxOrient = ''
  }, [lines])

  // Runs synchronously before paint so text/lines changes never flash the wrong mode
  useLayoutEffect(() => {
    measure()
  }, [text, measure])

  // ResizeObserver fires async after layout — useEffect is sufficient here.
  useEffect(() => {
    const el = linkRef.current
    if (!el) return
    const observer = new ResizeObserver(measure)
    observer.observe(el)
    return () => observer.disconnect()
  }, [measure])

  return (
    <a
      ref={linkRef}
      className={clsx(
        'dome-p2 dome-link dome-link-aqua',
        isClamped ? 'processing-page-address-link-clamped' : 'dome-link-arrow-right'
      )}
      style={{ overflowWrap: 'break-word', wordBreak: 'break-all' }}
      href={href}
      target={target}
      rel={target === '_blank' ? 'noopener noreferrer' : undefined}
    >
      <span
        ref={textRef}
        className={isClamped ? 'processing-page-address-text' : undefined}
      >
        {text}
      </span>
      {isClamped && <span className="processing-page-address-arrow">&hellip;&nbsp;&rarr;</span>}
    </a>
  )
}

export default ClampedLink
